import React, { useMemo } from "react";
import { Dialog, FormSelect, GridGenerator } from "@actinc/dls";
import { Minus as MinusIcon, Plus as PlusIcon } from "@actinc/dls/icons";
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import {
  ControlledFormDesktopDatePicker,
  ControlledFormEditor,
} from "../../../../components";
import { ReadyProvider } from "../../../../providers/ReadyProvider";
import { useExternalReviewerGroups } from "./useExternalReviewerGroups";

const SelectUserGroupsDialog = ({
  details,
  onClose,
  open,
  programs = [],
  roles,
  users,
}) => {
  const defaultValues = {
    assignedUsers: [],
    dueDate: null,
    instructions: "",
    reviewerGroupIds: [],
  };
  const formMethods = useForm({
    defaultValues: {
      details: [{ ...defaultValues }],
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const { control, getValues, handleSubmit } = formMethods;
  const {
    fields: groups,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: "details",
  });

  // The Groups picker only applies when assigning External Reviewers — both
  // entry points use this dialog (Search "Assign", and the Dashboard task
  // completion popup), so gate on the role rather than on the call site.
  // `roles` is a required prop (also used for the picker label below).
  const showReviewerGroups = roles.includes("External Reviewer");

  // External Reviewer Groups defined for the selected content's program(s),
  // plus the helper that resolves a section's group/reviewer selection into the
  // concrete reviewers to assign. See useExternalReviewerGroups for details.
  const { groupOptions, resolveAssignedUsers } = useExternalReviewerGroups({
    enabled: showReviewerGroups,
    programs,
    users,
  });

  const allGroupsHaveAssignedUser = useMemo(
    () => groups.every((group) => !!resolveAssignedUsers(group).length),
    [groups, resolveAssignedUsers],
  );

  const handleUserAdd = (index) => (event) => {
    const id = event.target.value;
    if (!id) return;
    const prev = getValues(`details[${index}]`);
    update(index, { ...prev, assignedUsers: [...prev.assignedUsers, id] });
  };

  const handleUserDelete = (index) => (value) => () => {
    const prev = getValues(`details[${index}]`);
    update(index, {
      ...prev,
      assignedUsers: prev.assignedUsers.filter((id) => id !== value),
    });
  };

  // The Groups picker is a multi-select like the reviewer picker: each pick
  // adds a group chip; chips are removed individually. While any group is
  // selected the reviewer picker is locked and vice versa (AC 3/4/5).
  const handleGroupAdd = (index) => (event) => {
    const groupId = event.target.value;
    if (!groupId) return;
    const prev = getValues(`details[${index}]`);
    if (prev.reviewerGroupIds.includes(groupId)) return;
    update(index, {
      ...prev,
      reviewerGroupIds: [...prev.reviewerGroupIds, groupId],
    });
  };

  const handleGroupDelete = (index) => (groupId) => () => {
    const prev = getValues(`details[${index}]`);
    update(index, {
      ...prev,
      reviewerGroupIds: prev.reviewerGroupIds.filter((id) => id !== groupId),
    });
  };

  const onSubmit = () => {
    const value = getValues("details")
      .map((section) => ({
        ...section,
        assignedUsers: resolveAssignedUsers(section),
      }))
      .filter((section) => !!section.assignedUsers.length)
      .map(({ assignedUsers, ...formData }) => {
        return ["dueDate", "instructions"].reduce(
          (acc, detail) => {
            if (!details.includes(detail)) return { ...acc };
            if (detail === "dueDate") {
              return {
                ...acc,
                dueDate:
                  formData.dueDate?.toISOString().split("T").at(0) || null,
              };
            }
            if (detail === "instructions") {
              return { ...acc, instructions: formData.instructions };
            }
          },
          { assignedUsers },
        );
      });
    onClose(value);
  };

  return (
    <FormProvider {...formMethods}>
      <Dialog maxWidth="md" onClose={() => onClose()} open={open}>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            Select Users
            <Button
              onClick={() => append({ ...defaultValues })}
              size="small"
              startIcon={<PlusIcon />}
              variant="outlined"
            >
              Add Task Group
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form id="selectUserForm" onSubmit={handleSubmit(onSubmit)}>
            {groups.map((group, index) => {
              // Reviewer groups and individual reviewers are mutually
              // exclusive: while any group is selected the reviewer picker is
              // locked (AC 3); once a reviewer is added manually the Groups
              // picker is locked (AC 4). Clearing one side re-enables the
              // other (AC 5).
              const hasReviewerGroups = !!group.reviewerGroupIds.length;
              const hasManualReviewers = !!group.assignedUsers.length;
              // Selected groups and reviewers share one chip row. Group chips
              // are "secondary"-colored to distinguish them from the "primary"
              // individual-reviewer chips.
              const chips = [
                ...group.reviewerGroupIds.map((groupId) => ({
                  color: "secondary",
                  key: `group-${groupId}`,
                  label:
                    groupOptions.find((opt) => opt.value === groupId)?.label ||
                    groupId,
                  onDelete: handleGroupDelete(index)(groupId),
                })),
                ...users
                  .filter((user) => group.assignedUsers.includes(user.value))
                  .map((user) => ({
                    color: "primary",
                    key: `user-${user.value}`,
                    label: user.label,
                    onDelete: handleUserDelete(index)(user.value),
                  })),
              ];
              return (
                <Box key={group.id} sx={{ marginBottom: 2 }}>
                  {!!details.length && (
                    <Divider>Task Group {index + 1}</Divider>
                  )}
                  {showReviewerGroups && (
                    <Typography sx={{ marginY: 1 }} variant="caption">
                      Select individual reviewer(s) or reviewer group(s)
                    </Typography>
                  )}
                  <Box
                    sx={{
                      alignItems: "baseline",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                        width: "75%",
                      }}
                    >
                      <FormSelect
                        disabled={hasReviewerGroups}
                        formControlProps={selectControlProps}
                        id={`details[${index}]-user`}
                        label={roles.join("/")}
                        onChange={handleUserAdd(index)}
                        options={users.map((user) => ({
                          ...user,
                          disabled: group.assignedUsers.includes(user.value),
                        }))}
                        value=""
                        variant="standard"
                      />
                      {showReviewerGroups && (
                        <FormSelect
                          disabled={hasManualReviewers}
                          formControlProps={selectControlProps}
                          id={`details[${index}]-group`}
                          label="Reviewer Group"
                          onChange={handleGroupAdd(index)}
                          options={groupOptions.map((option) => ({
                            ...option,
                            disabled: group.reviewerGroupIds.includes(
                              option.value,
                            ),
                          }))}
                          value=""
                          variant="standard"
                        />
                      )}
                    </Box>
                    {groups.length > 1 && (
                      <Button
                        onClick={(_e) => remove(index)}
                        size="small"
                        startIcon={<MinusIcon />}
                        variant="outlined"
                      >
                        Remove Task Group {index + 1}
                      </Button>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      minHeight: "32px",
                    }}
                  >
                    {chips.map(({ color, key, label, onDelete }) => (
                      <Chip
                        color={color}
                        key={key}
                        label={label}
                        onDelete={onDelete}
                        size="small"
                        sx={{ marginBottom: 1, marginRight: 1 }}
                      />
                    ))}
                  </Box>
                  <GridGenerator
                    groups={[
                      [
                        {
                          children: details.includes("dueDate") ? (
                            <ControlledFormDesktopDatePicker
                              control={control}
                              fullWidth={true}
                              id={`details[${index}].dueDate`}
                              label="Due Date"
                              name={`details[${index}].dueDate`}
                            />
                          ) : null,
                          key: `details[${index}].dueDate`,
                        },
                      ],
                      [
                        {
                          children: details.includes("instructions") ? (
                            <ReadyProvider onAllReady={() => {}}>
                              <ControlledFormEditor
                                control={control}
                                fullWidth={true}
                                id={`details[${index}].instructions`}
                                label="Instructions"
                                name={`details[${index}].instructions`}
                              />
                            </ReadyProvider>
                          ) : null,
                          key: `details[${index}].instructions`,
                        },
                      ],
                    ]}
                    key={index}
                  />
                </Box>
              );
            })}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} variant="text">
            Cancel
          </Button>
          <Button
            color="primary"
            form="selectUserForm"
            type="submit"
            variant="contained"
            disabled={!allGroupsHaveAssignedUser}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const selectControlProps = {
  margin: "none",
  size: "small",
  sx: {
    marginBottom: 2,
    minWidth: "50%",
    "& .MuiInputBase-root": {
      marginTop: 0,
    },
  },
};

export { externalReviewerGroupsQuery } from "./useExternalReviewerGroups";
export { SelectUserGroupsDialog };
